// File: src/pages/BankStatementReport.jsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Card,
  CardBody,
  Input,
  Row,
  Col,
  Label,
  Spinner,
  Table,
  Alert,
} from "reactstrap";
import { ImDownload } from "react-icons/im";
import { ChevronDown, ChevronRight } from "react-feather";
import jsPDF from "jspdf";
import "jspdf-autotable";
import * as XLSX from "xlsx";

import { _Get } from "../import/ApiEndPoint/ApiCalling";
import { View_Company_Details, View_Receipt } from "../import/ApiEndPoint/Api";
import SuperAdminUI from "../import/SuperAdminUI";
import { CheckPermission } from "../import/CheckPermission";

/* =========================================================
   CONSTANTS
========================================================= */
const CASH_ID = "CASH";
const MAX_PREVIOUS_FY_LOOKBACK = 10;

const ALL_COLS = [
  { id: "sno", label: "S.No" },
  { id: "date", label: "Date" },
  { id: "particular", label: "Particular" },
  { id: "debit", label: "Debit" },
  { id: "credit", label: "Credit" },
  { id: "balance", label: "Running Balance" },
];

/* =========================================================
   HELPERS
========================================================= */
const getUser = () => {
  try {
    return JSON.parse(localStorage.getItem("userData") || "{}");
  } catch {
    return {};
  }
};

const toStr = (v) => (v === null || v === undefined ? "" : String(v));

const norm = (v) => toStr(v).trim().toLowerCase();

const normalizeId = (v) => {
  if (!v) return "";
  if (typeof v === "string") return v;
  if (typeof v === "object") return toStr(v?._id || v?.id);
  return toStr(v);
};

const fmt2 = (v) => {
  const n = Number(v || 0);
  return Number.isFinite(n) ? n.toFixed(2) : "0.00";
};

const moneyWithDrCr = (num) => {
  const n = Number(num || 0);
  return `${Math.abs(n).toFixed(2)} ${n >= 0 ? "Cr" : "Dr"}`;
};

const ymd = (d) => {
  if (!d) return "";
  const dt = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(dt.getTime())) return "";

  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, "0");
  const da = String(dt.getDate()).padStart(2, "0");
  return `${y}-${m}-${da}`;
};

const parseTxnDate = (row) => {
  const raw = row?.date || row?.createdAt;
  const dt = new Date(raw);
  return Number.isNaN(dt.getTime()) ? null : dt;
};

const getTxnCreatedTs = (row) => {
  const dt = new Date(row?.createdAt || row?.date || 0);
  return Number.isNaN(dt.getTime()) ? 0 : dt.getTime();
};

const getTxnType = (row) => {
  const t = norm(row?.type);
  if (t.includes("payment")) return "payment";
  if (t.includes("receipt")) return "receipt";
  return "";
};

const getFYStartYear = (fyLabel) => {
  const m = toStr(fyLabel).match(/^(\d{4})-(\d{2}|\d{4})$/);
  if (!m) return null;
  return Number(m[1]);
};

const getCurrentFYLabel = () => {
  const now = new Date();
  const startYear =
    now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
  return `${startYear}-${String(startYear + 1).slice(-2)}`;
};

const previousFY = (fyLabel) => {
  const start = getFYStartYear(fyLabel);
  if (!Number.isFinite(start)) return "";
  return `${start - 1}-${String(start).slice(-2)}`;
};

const buildPreviousFYLabels = (
  selectedFY,
  count = MAX_PREVIOUS_FY_LOOKBACK,
) => {
  const out = [];
  let cursor = selectedFY;

  for (let i = 0; i < count; i += 1) {
    cursor = previousFY(cursor);
    if (!cursor) break;
    out.push(cursor);
  }

  return out;
};

const getDatabaseForFY = (baseDb, fy) => {
  const db = toStr(baseDb).trim();
  const fyText = toStr(fy).trim();

  if (!db || !fyText) return db;
  if (db.endsWith(`-${fyText}`)) return db;

  return `${db}-${fyText}`;
};

const getBankDetailsObject = (row) => {
  const bd = row?.bankDetails;

  if (Array.isArray(bd)) {
    return bd.find((x) => x && typeof x === "object") || null;
  }

  if (bd && typeof bd === "object") return bd;
  return null;
};

const getTxnBankId = (row) => {
  const bd = row?.bankDetails;

  if (Array.isArray(bd)) {
    const first = bd[0];
    if (typeof first === "string") return first;
    if (first && typeof first === "object") return normalizeId(first);
  }

  if (typeof bd === "string") return bd;
  if (bd && typeof bd === "object") return normalizeId(bd);

  return normalizeId(row?.bankId || row?.bankDetailsId);
};

const getTxnBankName = (row) => {
  const bd = getBankDetailsObject(row);

  return toStr(
    bd?.bankName ||
      row?.bankName ||
      row?.bankSelect ||
      row?.expenseId?.bankName ||
      row?.expenseId?.title,
  ).trim();
};

const getTxnBankAccount = (row) => {
  const bd = getBankDetailsObject(row);

  return toStr(
    bd?.accountNumber ||
      row?.accountNumber ||
      row?.bankAccountNumber ||
      row?.bankAccNo,
  ).trim();
};

const getParticularText = (row) => {
  const userName = row?.userId
    ? `${row?.userId?.firstName || ""} ${row?.userId?.lastName || ""}`.trim()
    : "";

  return (
    row?.expenseId?.title ||
    row?.expenseId?.bankName ||
    row?.partyId?.CompanyName ||
    userName ||
    row?.transporterId?.companyName ||
    row?.bankDetails?.bankName ||
    row?.bankName ||
    (typeof row?.partyId === "string" ? row.partyId : "") ||
    "NA"
  );
};

const maskAcc = (acc) => {
  const s = toStr(acc).replace(/\s+/g, "");
  if (!s) return "";
  if (s.length <= 4) return s;
  return `XXXXXX${s.slice(-4)}`;
};

const bankLabel = (b) => {
  if (!b) return "Bank";

  const name = toStr(b?.bankName).trim() || "Bank";
  const account = maskAcc(b?.accountNumber);
  const branch = toStr(b?.branchName).trim();

  return `${name}${account ? ` (${account})` : ""}${branch ? ` - ${branch}` : ""}`;
};

const isCashTransaction = (row) => {
  const bankId = norm(getTxnBankId(row));
  const bankName = norm(getTxnBankName(row));
  const paymentMode = norm(row?.paymentMode);

  return (
    bankId === norm(CASH_ID) || bankName === "cash" || paymentMode === "cash"
  );
};

const rowBelongsToBank = (row, selectedBankId, selectedBankObj) => {
  if (!row || !selectedBankId || !selectedBankObj) return false;
  if (!getTxnType(row)) return false;
  if (isCashTransaction(row)) return false;

  const selectedId = norm(selectedBankId);
  const selectedName = norm(selectedBankObj?.bankName);
  const selectedAccount = toStr(selectedBankObj?.accountNumber).trim();

  const rowId = norm(getTxnBankId(row));
  const rowName = norm(getTxnBankName(row));
  const rowAccount = toStr(getTxnBankAccount(row)).trim();

  if (rowId && rowId === selectedId) return true;
  if (selectedName && rowName && rowName === selectedName) return true;
  if (selectedAccount && rowAccount && rowAccount === selectedAccount)
    return true;

  return false;
};

const signedOpeningForBank = (bank) => {
  const amount = Math.abs(Number(bank?.openingBalance || 0));
  const type = norm(bank?.openingType || "credit");
  return type === "debit" ? -amount : amount;
};

const monthKeyForDate = (d) => {
  const dt = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(dt.getTime())) return "";
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}`;
};

const monthLabelForDate = (d) => {
  const dt = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(dt.getTime())) return "";

  return `${dt.toLocaleString("en-US", { month: "long" })} - ${dt.getFullYear()}`;
};

const injectOnce = () => {
  if (document.getElementById("bank-statement-inline-css-v10")) return;

  const s = document.createElement("style");
  s.id = "bank-statement-inline-css-v10";
  s.textContent = `
    .bs-card{
      border-radius:14px;
      border:1px solid #e5e7eb;
      overflow:hidden;
      background:#fff;
    }

    .bs-head{
      padding:10px 12px;
      display:flex;
      align-items:flex-end;
      gap:10px;
      flex-wrap:nowrap;
      background:#f8fafc;
      border-bottom:1px solid #e5e7eb;
      overflow-x:auto;
    }

    .bs-title-wrap{
      display:flex;
      flex-direction:column;
      min-width:max-content;
      margin-right:4px;
    }

    .bs-title{
      font-weight:800;
      font-size:18px;
      line-height:1.1;
      white-space:nowrap;
    }

    .bs-pill{
      background:#eef2ff;
      color:#3730a3;
      font-weight:700;
      padding:4px 10px;
      border-radius:999px;
      font-size:11px;
      margin-top:4px;
      width:max-content;
      white-space:nowrap;
    }

    .bs-toolbar{
      display:flex;
      gap:8px;
      align-items:flex-end;
      flex-wrap:nowrap;
      min-width:max-content;
      margin-left:auto;
    }

    .bs-field{
      min-width:190px;
      margin-bottom:0;
    }

    .bs-field-lg{
      min-width:230px;
      margin-bottom:0;
    }

    .bs-field label,
    .bs-field-lg label{
      font-size:11px;
      font-weight:700;
      color:#475569;
      margin-bottom:4px;
      white-space:nowrap;
    }

    .bs-iconbtn{
      cursor:pointer;
      padding:8px 12px;
      border:1px solid #cbd5e1;
      color:#0f766e;
      border-radius:10px;
      display:inline-flex;
      align-items:center;
      gap:6px;
      background:#fff;
      font-weight:700;
      height:38px;
      white-space:nowrap;
    }

    .bs-menu{
      position:absolute;
      right:0;
      top:40px;
      z-index:20;
      background:#fff;
      border:1px solid #39cccc;
      border-radius:10px;
      padding:8px;
      min-width:130px;
      box-shadow:0 12px 24px rgba(0,0,0,.12);
    }

    .bs-click{
      cursor:pointer;
      border-radius:8px;
    }

    .bs-click:hover{
      background:#f1f5f9;
    }

    .bs-table-wrap{
      width:100%;
      overflow:auto;
    }

    .bs-table th{
      background:#f4f7f6;
      font-size:12px;
      text-transform:uppercase;
      letter-spacing:.03em;
      position:sticky;
      top:0;
      z-index:2;
      white-space:nowrap;
      text-align:center;
    }

    .bs-table td{
      vertical-align:middle;
      text-align:center;
    }

    .bs-month-row td{
      background:#fae9bb !important;
      font-weight:800;
      cursor:pointer;
      border-top:2px solid #e3c56d;
    }

    .bs-month-row:hover td{
      background:#f7df9c !important;
    }

    .bs-detail-row td{
      background:#fff;
    }

    .bs-detail-row:hover td{
      background:#f8fafc;
    }

    .bs-detail-particular{
      padding-left:34px !important;
      text-align:left !important;
    }

    .bs-month-particular{
      display:flex;
      align-items:center;
      gap:7px;
      justify-content:flex-start;
      text-align:left;
      white-space:nowrap;
    }

    .bs-open-row td{
      background:#fffbeb !important;
      font-weight:800;
      border-top:2px solid #f59e0b;
    }

    .bs-topstat{
      min-width:145px;
      height:54px;
      border-radius:12px;
      padding:7px 10px;
      display:flex;
      flex-direction:column;
      justify-content:center;
      background:#fff;
      border:1px solid #e5e7eb;
      white-space:nowrap;
    }

    .bs-topstat-label{
      font-size:10px;
      font-weight:800;
      text-transform:uppercase;
      color:#64748b;
      line-height:1.1;
      margin-bottom:4px;
    }

    .bs-topstat-value{
      font-size:14px;
      font-weight:800;
      line-height:1.1;
    }

    .bs-topstat.opening .bs-topstat-value{ color:#2563eb; }
    .bs-topstat.debit .bs-topstat-value{ color:#dc2626; }
    .bs-topstat.credit .bs-topstat-value{ color:#16a34a; }
    .bs-topstat.closing.pos .bs-topstat-value{ color:#16a34a; }
    .bs-topstat.closing.neg .bs-topstat-value{ color:#dc2626; }

    .bs-bottombar{
      display:flex;
      justify-content:flex-end;
      align-items:center;
      gap:10px;
      padding:10px 14px;
      border-top:1px solid #e5e7eb;
      background:#fff;
    }

    .bs-resultnote{
      margin-right:auto;
      color:#64748b;
      font-size:12px;
      font-weight:600;
      white-space:nowrap;
    }
  `;

  document.head.appendChild(s);
};

/* =========================================================
   COMPONENT
========================================================= */
export default function BankStatementReport() {
  useEffect(() => injectOnce(), []);

  const selectedFY =
    localStorage.getItem("financialYearSelected") || getCurrentFYLabel();

  const [permissions, setPermissions] = useState({
    View: true,
    Download: true,
  });

  const [masterShow, setMasterShow] = useState(false);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");

  const [baseDatabase, setBaseDatabase] = useState("");
  const [companyBanks, setCompanyBanks] = useState([]);
  const [selectedBank, setSelectedBank] = useState("");

  // { "2026-27": [...receipt/payment rows], "2025-26": [...], ... }
  const [transactionsByFY, setTransactionsByFY] = useState({});

  const [search, setSearch] = useState("");
  const [downloadOpen, setDownloadOpen] = useState(false);
  const [expandedMonths, setExpandedMonths] = useState(() => new Set());

  const COL_KEY = "BankStatementReportCols";
  const [selectedColIds] = useState(() => {
    const raw = localStorage.getItem(COL_KEY);

    try {
      const parsed = raw ? JSON.parse(raw) : null;
      if (Array.isArray(parsed) && parsed.length) {
        const valid = parsed.filter((id) => ALL_COLS.some((c) => c.id === id));
        if (valid.length) return valid;
      }
    } catch {}

    return ALL_COLS.map((c) => c.id);
  });

  /* ---------------------------------------------------------
     BANK OPTIONS
     IMPORTANT: Cash is intentionally NOT added to this list.
  --------------------------------------------------------- */
  const realBankAccounts = useMemo(() => {
    return (companyBanks || []).filter((b) => {
      const id = norm(b?._id);
      const name = norm(b?.bankName);
      return id !== norm(CASH_ID) && name !== "cash" && b?.isCash !== true;
    });
  }, [companyBanks]);

  const selectedBankObj = useMemo(() => {
    return (
      realBankAccounts.find((b) => toStr(b?._id) === toStr(selectedBank)) ||
      null
    );
  }, [realBankAccounts, selectedBank]);

  const visibleCols = useMemo(
    () => ALL_COLS.filter((c) => selectedColIds.includes(c.id)),
    [selectedColIds],
  );

  /* ---------------------------------------------------------
     API LOADERS
  --------------------------------------------------------- */
  const fetchReceiptPaymentRows = useCallback(
    async (db, fy, silent = false) => {
      if (!db || !fy) return [];

      try {
        const selectedDatabase = getDatabaseForFY(db, fy);
        const res = await _Get(View_Receipt, selectedDatabase);
        const list = Array.isArray(res?.Receipts) ? res.Receipts : [];

        return list.filter((row) => {
          const type = getTxnType(row);
          return type === "receipt" || type === "payment";
        });
      } catch (e) {
        if (e?.response?.status === 404) return [];
        if (silent) return [];
        throw e;
      }
    },
    [],
  );

  const loadCompanyBanks = useCallback(async (uid, db) => {
    const url = `${View_Company_Details}${encodeURIComponent(uid)}/`;
    const res = await _Get(url, db);

    const detail = res?.CompanyDetail || {};
    const banks = Array.isArray(detail?.bankDetails) ? detail.bankDetails : [];

    const realBanks = banks.filter((b) => {
      const id = norm(b?._id);
      const name = norm(b?.bankName);
      return id !== norm(CASH_ID) && name !== "cash" && b?.isCash !== true;
    });

    setCompanyBanks(realBanks);

    const preferredName = norm(detail?.selectedBank);
    const preferred = preferredName
      ? realBanks.find((b) => norm(b?.bankName) === preferredName)
      : null;

    const first = preferred || realBanks[0] || null;
    const firstId = first?._id ? toStr(first._id) : "";

    setSelectedBank(firstId);

    return {
      detail,
      banks: realBanks,
      selectedBankId: firstId,
    };
  }, []);

  const loadFinancialYearChain = useCallback(
    async (db, fy) => {
      const result = {};

      // Current selected FY is mandatory.
      result[fy] = await fetchReceiptPaymentRows(db, fy, false);

      // Load previous FYs only until the first completely empty FY.
      // This gives us enough history to calculate a carried-forward opening.
      const previousLabels = buildPreviousFYLabels(
        fy,
        MAX_PREVIOUS_FY_LOOKBACK,
      );

      for (const prevFy of previousLabels) {
        const rows = await fetchReceiptPaymentRows(db, prevFy, true);
        result[prevFy] = rows;

        // User requirement: once the immediately older FY has no data,
        // the bank's own opening balance becomes the opening source.
        if (!rows.length) break;
      }

      setTransactionsByFY(result);
      return result;
    },
    [fetchReceiptPaymentRows],
  );

  const loadFor = useCallback(
    async (uid, db) => {
      if (!uid || !db) {
        setCompanyBanks([]);
        setSelectedBank("");
        setTransactionsByFY({});
        setBaseDatabase("");
        return;
      }

      setBaseDatabase(db);

      const companyResult = await loadCompanyBanks(uid, db);
      await loadFinancialYearChain(db, selectedFY);

      return companyResult;
    },
    [loadCompanyBanks, loadFinancialYearChain, selectedFY],
  );

  const reloadDefault = useCallback(async () => {
    const user = getUser();
    const uid = user?._id || user?.id || "";
    const db = user?.database || user?.db || "";

    setMasterShow(user?.rolename?.roleName === "MASTER");

    const inside = CheckPermission("Bank Statement");
    if (inside && typeof inside === "object") {
      setPermissions((prev) => ({ ...prev, ...inside }));
    }

    await loadFor(uid, db);
  }, [loadFor]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      setErrorMsg("");

      try {
        await reloadDefault();
      } catch (e) {
        console.error("[BankStatementReport] load error:", e);
        setErrorMsg(
          e?.response?.data?.message ||
            e?.message ||
            "Unable to load bank statement data.",
        );
        setCompanyBanks([]);
        setSelectedBank("");
        setTransactionsByFY({});
      } finally {
        setLoading(false);
      }
    })();
  }, [reloadDefault]);

  const handleMasterSubmit = async (e) => {
    e?.preventDefault?.();

    const raw = localStorage.getItem("SuperadminIdByMaster");

    try {
      setLoading(true);
      setErrorMsg("");

      if (raw) {
        const parsed = JSON.parse(raw);
        const [id, db] = toStr(parsed).split(" ");

        if (id && db) {
          await loadFor(id, db);
        } else {
          await reloadDefault();
        }
      } else {
        await reloadDefault();
      }
    } catch (e2) {
      console.error("[BankStatementReport] master load error:", e2);
      setErrorMsg(
        e2?.response?.data?.message ||
          e2?.message ||
          "Unable to load bank statement data.",
      );
      setCompanyBanks([]);
      setSelectedBank("");
      setTransactionsByFY({});
    } finally {
      setLoading(false);
    }
  };

  /* ---------------------------------------------------------
     SELECTED BANK TRANSACTIONS BY FY
  --------------------------------------------------------- */
  const selectedBankRowsByFY = useMemo(() => {
    const out = {};

    Object.entries(transactionsByFY || {}).forEach(([fy, rows]) => {
      out[fy] = (rows || []).filter((row) =>
        rowBelongsToBank(row, selectedBank, selectedBankObj),
      );
    });

    return out;
  }, [transactionsByFY, selectedBank, selectedBankObj]);

  /* ---------------------------------------------------------
     OPENING BALANCE

     Rule implemented:
     1. Start from selected bank account's own opening balance.
     2. Check the immediately previous FY.
     3. If that FY has transactions for THIS bank, its closing balance is
        carried into the selected FY.
     4. Keep walking backward only while every previous FY has bank data.
     5. At the first FY with no bank data, stop. Older FYs are ignored and the
        bank account opening balance is the base.
  --------------------------------------------------------- */
  const openingBalance = useMemo(() => {
    if (!selectedBankObj) return 0;

    let opening = signedOpeningForBank(selectedBankObj);
    const contiguousPreviousYears = [];

    let cursor = previousFY(selectedFY);

    for (let i = 0; i < MAX_PREVIOUS_FY_LOOKBACK && cursor; i += 1) {
      const rows = selectedBankRowsByFY[cursor] || [];

      if (!rows.length) break;

      contiguousPreviousYears.push({ fy: cursor, rows });
      cursor = previousFY(cursor);
    }

    // We collected newest previous FY first.
    // Apply oldest -> newest to obtain the previous FY closing correctly.
    contiguousPreviousYears.reverse().forEach(({ rows }) => {
      const sorted = [...rows].sort((a, b) => {
        const da = parseTxnDate(a)?.getTime() || 0;
        const db = parseTxnDate(b)?.getTime() || 0;
        if (da !== db) return da - db;
        return getTxnCreatedTs(a) - getTxnCreatedTs(b);
      });

      sorted.forEach((row) => {
        const amount = Number(row?.amount || 0);
        const type = getTxnType(row);

        if (type === "receipt") opening += amount;
        if (type === "payment") opening -= amount;
      });
    });

    return opening;
  }, [selectedBankObj, selectedBankRowsByFY, selectedFY]);

  /* ---------------------------------------------------------
     CURRENT FY ROWS + RUNNING BALANCE

     Running balance is ALWAYS calculated oldest -> newest.
     UI later displays newest -> oldest.
  --------------------------------------------------------- */
  const currentFYRowsAsc = useMemo(() => {
    const rows = [...(selectedBankRowsByFY[selectedFY] || [])].sort((a, b) => {
      const da = parseTxnDate(a)?.getTime() || 0;
      const db = parseTxnDate(b)?.getTime() || 0;
      if (da !== db) return da - db;
      return getTxnCreatedTs(a) - getTxnCreatedTs(b);
    });

    let running = Number(openingBalance || 0);

    return rows.map((row, index) => {
      const dateObj = parseTxnDate(row);
      const amount = Number(row?.amount || 0);
      const type = getTxnType(row);

      const debit = type === "payment" ? amount : 0;
      const credit = type === "receipt" ? amount : 0;

      running = running + credit - debit;

      const particular = getParticularText(row);
      const remark = toStr(row?.remark).trim();
      const fullParticular = remark ? `${particular} - ${remark}` : particular;

      return {
        _id: row?._id || `${dateObj?.getTime() || 0}-${index}`,
        _raw: row,
        _dateObj: dateObj,
        _ts: dateObj?.getTime() || 0,
        _createdTs: getTxnCreatedTs(row),
        date: ymd(dateObj),
        particular: fullParticular,
        debit,
        credit,
        running,
      };
    });
  }, [selectedBankRowsByFY, selectedFY, openingBalance]);

  const currentFYRowsDesc = useMemo(() => {
    return [...currentFYRowsAsc].sort((a, b) => {
      if (a._ts !== b._ts) return b._ts - a._ts;
      return b._createdTs - a._createdTs;
    });
  }, [currentFYRowsAsc]);

  /* ---------------------------------------------------------
     MONTH GROUPS
  --------------------------------------------------------- */
  const monthGroups = useMemo(() => {
    const map = new Map();

    currentFYRowsAsc.forEach((row) => {
      const key = monthKeyForDate(row._dateObj);
      if (!key) return;

      if (!map.has(key)) {
        map.set(key, {
          key,
          label: monthLabelForDate(row._dateObj),
          sortTs: new Date(
            row._dateObj.getFullYear(),
            row._dateObj.getMonth(),
            1,
          ).getTime(),
          debit: 0,
          credit: 0,
          running: row.running,
          rowsAsc: [],
        });
      }

      const group = map.get(key);
      group.debit += Number(row.debit || 0);
      group.credit += Number(row.credit || 0);
      group.running = Number(row.running || 0); // month's closing balance
      group.rowsAsc.push(row);
    });

    return Array.from(map.values())
      .map((group) => ({
        ...group,
        rowsDesc: [...group.rowsAsc].sort((a, b) => {
          if (a._ts !== b._ts) return b._ts - a._ts;
          return b._createdTs - a._createdTs;
        }),
      }))
      .sort((a, b) => b.sortTs - a.sortTs);
  }, [currentFYRowsAsc]);

  const searchTerm = search.trim().toLowerCase();

  const filteredMonthGroups = useMemo(() => {
    if (!searchTerm) return monthGroups;

    return monthGroups
      .map((group) => {
        const monthMatches = group.label.toLowerCase().includes(searchTerm);

        const matchingRows = group.rowsDesc.filter((row) =>
          [
            row.date,
            row.particular,
            fmt2(row.debit),
            fmt2(row.credit),
            moneyWithDrCr(row.running),
          ].some((value) => toStr(value).toLowerCase().includes(searchTerm)),
        );

        if (!monthMatches && !matchingRows.length) return null;

        return {
          ...group,
          visibleRows: monthMatches ? group.rowsDesc : matchingRows,
        };
      })
      .filter(Boolean);
  }, [monthGroups, searchTerm]);

  const toggleMonth = (key) => {
    setExpandedMonths((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  /* ---------------------------------------------------------
     TOTALS
  --------------------------------------------------------- */
  const totals = useMemo(() => {
    const debit = currentFYRowsAsc.reduce(
      (sum, row) => sum + Number(row.debit || 0),
      0,
    );
    const credit = currentFYRowsAsc.reduce(
      (sum, row) => sum + Number(row.credit || 0),
      0,
    );

    const closingBalance = currentFYRowsAsc.length
      ? Number(currentFYRowsAsc[currentFYRowsAsc.length - 1]?.running || 0)
      : Number(openingBalance || 0);

    return {
      openingBal: Number(openingBalance || 0),
      debit,
      credit,
      closingBalance,
    };
  }, [currentFYRowsAsc, openingBalance]);

  /* ---------------------------------------------------------
     EXPORT ROWS
     Export stays transaction-level, newest first, with Opening Balance last.
  --------------------------------------------------------- */
  const exportRows = useMemo(() => {
    let rows = currentFYRowsDesc;

    if (searchTerm) {
      rows = rows.filter((row) =>
        [
          row.date,
          row.particular,
          fmt2(row.debit),
          fmt2(row.credit),
          moneyWithDrCr(row.running),
        ].some((value) => toStr(value).toLowerCase().includes(searchTerm)),
      );
    }

    const mapped = rows.map((row, index) => ({
      sno: index + 1,
      date: row.date,
      particular: row.particular,
      debit: row.debit ? fmt2(row.debit) : "",
      credit: row.credit ? fmt2(row.credit) : "",
      balance: moneyWithDrCr(row.running),
    }));

    mapped.push({
      sno: "",
      date: "",
      particular: `Opening Balance - FY ${selectedFY}`,
      debit: "",
      credit: "",
      balance: moneyWithDrCr(openingBalance),
    });

    return mapped;
  }, [currentFYRowsDesc, searchTerm, selectedFY, openingBalance]);

  const exportPDF = () => {
    if (!selectedBankObj) return;

    const doc = new jsPDF("landscape");
    const head = [visibleCols.map((c) => c.label)];

    const body = exportRows.map((row) =>
      visibleCols.map((c) => {
        if (c.id === "sno") return row.sno;
        if (c.id === "date") return row.date;
        if (c.id === "particular") return row.particular;
        if (c.id === "debit") return row.debit;
        if (c.id === "credit") return row.credit;
        if (c.id === "balance") return row.balance;
        return "";
      }),
    );

    doc.text(`Bank Statement - FY ${selectedFY}`, 14, 12);
    doc.text(bankLabel(selectedBankObj), 14, 18);

    doc.autoTable({
      head,
      body,
      startY: 24,
      styles: { fontSize: 8 },
      headStyles: { fillColor: [244, 247, 246] },
    });

    doc.save(`BankStatement-${selectedFY}.pdf`);
  };

  const exportXLSX = () => {
    if (!selectedBankObj) return;

    const data = exportRows.map((row) => {
      const obj = {};

      visibleCols.forEach((c) => {
        obj[c.label] = row[c.id] ?? "";
      });

      return obj;
    });

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Bank Statement");
    XLSX.writeFile(wb, `BankStatement-${selectedFY}.xlsx`);
  };

  /* ---------------------------------------------------------
     LOADING
  --------------------------------------------------------- */
  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          marginTop: "20rem",
        }}
      >
        <Spinner style={{ height: "4rem", width: "4rem" }} color="primary">
          Loading...
        </Spinner>
      </div>
    );
  }

  /* ---------------------------------------------------------
     RENDER CELL HELPERS
  --------------------------------------------------------- */
  const renderMonthCell = (colId, group, expanded) => {
    if (colId === "sno") return "";
    if (colId === "date") return "";

    if (colId === "particular") {
      return (
        <div className="bs-month-particular">
          {expanded ? <ChevronDown size={17} /> : <ChevronRight size={17} />}
          <span>{group.label}</span>
        </div>
      );
    }

    if (colId === "debit") return group.debit ? fmt2(group.debit) : "";
    if (colId === "credit") return group.credit ? fmt2(group.credit) : "";
    if (colId === "balance") return moneyWithDrCr(group.running);

    return "";
  };

  const renderDetailCell = (colId, row, sno) => {
    if (colId === "sno") return sno;
    if (colId === "date") return row.date;
    if (colId === "particular") return row.particular;
    if (colId === "debit") return row.debit ? fmt2(row.debit) : "";
    if (colId === "credit") return row.credit ? fmt2(row.credit) : "";
    if (colId === "balance") return moneyWithDrCr(row.running);
    return "";
  };

  return (
    <>
      {errorMsg ? (
        <Row>
          <Col sm="12">
            <Alert color="danger" style={{ marginBottom: 10 }}>
              {errorMsg}
            </Alert>
          </Col>
        </Row>
      ) : null}

      <Card className="bs-card">
        <div className="bs-head">
          <div className="bs-title-wrap">
            <div className="bs-title">Bank Statement</div>
            <span className="bs-pill">
              FY {selectedFY} • {currentFYRowsDesc.length} entries
            </span>
          </div>

          {masterShow ? (
            <div style={{ minWidth: 240 }}>
              <SuperAdminUI
                onDropdownChange={(v) =>
                  localStorage.setItem(
                    "SuperadminIdByMaster",
                    JSON.stringify(v),
                  )
                }
                onSubmit={handleMasterSubmit}
              />
            </div>
          ) : null}

          <div className="bs-toolbar">
            <div className="bs-field bs-field-lg">
              <Label>Search</Label>
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search date / particular..."
              />
            </div>

            <div className="bs-field">
              <Label>Bank Account</Label>
              <Input
                type="select"
                value={selectedBank}
                onChange={(e) => {
                  setSelectedBank(e.target.value);
                  setExpandedMonths(new Set());
                }}
              >
                <option value="">-- Select Bank Account --</option>
                {realBankAccounts.map((b) => (
                  <option key={toStr(b?._id)} value={toStr(b?._id)}>
                    {bankLabel(b)}
                  </option>
                ))}
              </Input>
            </div>

            <div className="bs-topstat opening">
              <div className="bs-topstat-label">Opening</div>
              <div className="bs-topstat-value">
                ₹ {moneyWithDrCr(totals.openingBal)}
              </div>
            </div>

            <div className="bs-topstat debit">
              <div className="bs-topstat-label">Debit</div>
              <div className="bs-topstat-value">₹ {fmt2(totals.debit)}</div>
            </div>

            <div className="bs-topstat credit">
              <div className="bs-topstat-label">Credit</div>
              <div className="bs-topstat-value">₹ {fmt2(totals.credit)}</div>
            </div>

            <div
              className={`bs-topstat closing ${
                totals.closingBalance < 0 ? "neg" : "pos"
              }`}
            >
              <div className="bs-topstat-label">Closing</div>
              <div className="bs-topstat-value">
                ₹ {moneyWithDrCr(totals.closingBalance)}
              </div>
            </div>

            {permissions?.Download && (
              <div
                className="bs-iconbtn"
                onMouseEnter={() => setDownloadOpen(true)}
                onMouseLeave={() => setDownloadOpen(false)}
                style={{ position: "relative" }}
                title="Download"
              >
                <ImDownload /> Download
                {downloadOpen && (
                  <div className="bs-menu">
                    <div
                      className="bs-click"
                      style={{ padding: 6 }}
                      onClick={exportPDF}
                    >
                      .PDF
                    </div>
                    <div
                      className="bs-click"
                      style={{ padding: 6 }}
                      onClick={exportXLSX}
                    >
                      .XLSX
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <CardBody style={{ padding: 0 }}>
          <div className="bs-table-wrap">
            <Table responsive hover bordered className="bs-table mb-0">
              <thead>
                <tr>
                  {visibleCols.map((c) => (
                    <th key={c.id}>{c.label}</th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {!selectedBank ? (
                  <tr>
                    <td
                      colSpan={visibleCols.length}
                      style={{
                        textAlign: "center",
                        padding: 16,
                        color: "#64748b",
                      }}
                    >
                      Please select a bank account.
                    </td>
                  </tr>
                ) : (
                  <>
                    {filteredMonthGroups.length === 0 ? (
                      <tr>
                        <td
                          colSpan={visibleCols.length}
                          style={{
                            textAlign: "center",
                            padding: 16,
                            color: "#64748b",
                          }}
                        >
                          No receipt/payment bank entries found for FY{" "}
                          {selectedFY}.
                        </td>
                      </tr>
                    ) : (
                      filteredMonthGroups.map((group) => {
                        const expanded = searchTerm
                          ? true
                          : expandedMonths.has(group.key);

                        const childRows = searchTerm
                          ? group.visibleRows || []
                          : group.rowsDesc;

                        return (
                          <React.Fragment key={group.key}>
                            <tr
                              className="bs-month-row"
                              onClick={() => toggleMonth(group.key)}
                              title="Click to expand/collapse month"
                            >
                              {visibleCols.map((c) => {
                                const isBalance = c.id === "balance";
                                const value = renderMonthCell(
                                  c.id,
                                  group,
                                  expanded,
                                );

                                return (
                                  <td
                                    key={c.id}
                                    style={
                                      isBalance
                                        ? {
                                            fontWeight: 800,
                                            color:
                                              Number(group.running || 0) < 0
                                                ? "#dc2626"
                                                : "#16a34a",
                                          }
                                        : undefined
                                    }
                                  >
                                    {value}
                                  </td>
                                );
                              })}
                            </tr>

                            {expanded &&
                              childRows.map((row) => {
                                // S.No is based on the full newest-first list.
                                const globalIndex = currentFYRowsDesc.findIndex(
                                  (x) => x._id === row._id,
                                );
                                const sno =
                                  globalIndex >= 0 ? globalIndex + 1 : "";

                                return (
                                  <tr key={row._id} className="bs-detail-row">
                                    {visibleCols.map((c) => {
                                      const isBalance = c.id === "balance";
                                      const isParticular =
                                        c.id === "particular";

                                      return (
                                        <td
                                          key={c.id}
                                          className={
                                            isParticular
                                              ? "bs-detail-particular"
                                              : ""
                                          }
                                          style={
                                            isBalance
                                              ? {
                                                  fontWeight: 700,
                                                  color:
                                                    Number(row.running || 0) < 0
                                                      ? "#dc2626"
                                                      : "#16a34a",
                                                }
                                              : undefined
                                          }
                                        >
                                          {renderDetailCell(c.id, row, sno)}
                                        </td>
                                      );
                                    })}
                                  </tr>
                                );
                              })}
                          </React.Fragment>
                        );
                      })
                    )}

                    {/* Opening Balance is intentionally ALWAYS at the bottom */}
                    <tr className="bs-open-row">
                      {visibleCols.map((c) => {
                        let value = "";

                        if (c.id === "particular") {
                          value = `Opening Balance - FY ${selectedFY}`;
                        } else if (c.id === "balance") {
                          value = moneyWithDrCr(openingBalance);
                        }

                        return (
                          <td
                            key={c.id}
                            style={
                              c.id === "balance"
                                ? {
                                    fontWeight: 800,
                                    color:
                                      Number(openingBalance || 0) < 0
                                        ? "#dc2626"
                                        : "#16a34a",
                                  }
                                : undefined
                            }
                          >
                            {value}
                          </td>
                        );
                      })}
                    </tr>
                  </>
                )}
              </tbody>
            </Table>
          </div>

          <div className="bs-bottombar">
            <div className="bs-resultnote">
              Total Entries: <b>{currentFYRowsDesc.length}</b>
              {baseDatabase ? (
                <span style={{ marginLeft: 12 }}>
                  Database: <b>{getDatabaseForFY(baseDatabase, selectedFY)}</b>
                </span>
              ) : null}
            </div>
          </div>
        </CardBody>
      </Card>
    </>
  );
}
